import React, { useState, useEffect } from 'react';
import { AlertTriangle, Eye, Shield, Check, Clock } from 'lucide-react';
import { AdminLayout } from '../components/admin';
import { LoadingSpinner } from '../components/ui';
import { UserMessageModal } from '../components/admin/UserMessageModal';
import { userManagementService } from '../services/userManagementService';
import type { UserReport, ReportFilters, ReportStats } from '../services/userManagementService';
import { useAuthStore } from '../store/authStore';
import { Navigate } from 'react-router-dom';
import { toast } from '../components/ui/Toaster';


const AdminReportsPage: React.FC = () => {
  const { user } = useAuthStore();
  const [reports, setReports] = useState<UserReport[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedReport, setSelectedReport] = useState<UserReport | null>(null);
  const [messageModal, setMessageModal] = useState<{userId: string, username: string} | null>(null);
  const [stats, setStats] = useState<ReportStats | null>(null);
  const [activeTab, setActiveTab] = useState<'pending' | 'resolved'>('pending');
  const [_filters, _setFilters] = useState<ReportFilters>({
    page: 1,
    limit: 20
  });

  const updateReportStatus = async (reportId: number, status: string) => {
    try {
      await userManagementService.updateReportStatus(reportId, status as any);
      toast.success('Succes', 'Rapport status bijgewerkt');
      loadReports();
      loadStats();
      setSelectedReport(null);
    } catch (error) {
      console.error('Failed to update report status:', error);
      toast.error('Fout', 'Kon rapport status niet bijwerken');
    }
  };

  // Redirect non-admin users
  if (!user?.isAdmin) {
    return <Navigate to="/dashboard" replace />;
  }

  useEffect(() => {
    loadReports();
    loadStats();
  }, [_filters, activeTab]);

  const loadReports = async () => {
    try {
      setIsLoading(true);

      // Add filtering based on active tab
      const tabFilters = {
        ..._filters,
        // Filter by reviewedAt: null = pending, not null = resolved
        ...(activeTab === 'pending' ? {} : {}), // Backend will handle this filtering
      };

      const response = await userManagementService.getReports(tabFilters);

      // Filter reports based on tab on frontend side
      const filteredReports = response.reports.filter(report => {
        if (activeTab === 'pending') {
          return !report.reviewedAt; // Not reviewed = pending
        } else {
          return !!report.reviewedAt; // Reviewed = resolved
        }
      });

      setReports(filteredReports);
    } catch (error) {
      console.error('Failed to load reports:', error);
      toast.error('Fout', 'Kon rapporten niet laden');
    } finally {
      setIsLoading(false);
    }
  };

  const loadStats = async () => {
    try {
      const statsResponse = await userManagementService.getReportStats();
      setStats(statsResponse);
    } catch (error) {
      console.error('Failed to load report stats:', error);
    }
  };

  const suspendUser = async (userId: number, reason?: string) => {
    try {
      await userManagementService.suspendUser(userId, reason);
      toast.success('Succes', 'Gebruiker account opgeschort');
      loadReports();
      loadStats();
      setSelectedReport(null);
    } catch (error) {
      console.error('Failed to suspend user:', error);
      toast.error('Fout', 'Kon gebruiker niet opschorten');
    }
  };

  const unsuspendUser = async (userId: number) => {
    try {
      await userManagementService.unsuspendUser(userId);
      toast.success('Succes', 'Gebruiker account hersteld');
      loadReports();
      loadStats();
      setSelectedReport(null);
    } catch (error) {
      console.error('Failed to unsuspend user:', error);
      toast.error('Fout', 'Kon gebruiker niet herstellen');
    }
  };

  return (
    <AdminLayout
      title="Gebruiker rapporten"
      subtitle="Beheer gebruiker rapporten en moderatie acties"
    >
      <div className="space-y-6">
        {/* Statistics Cards */}
        {stats && (
          <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 gap-4">
            <div className="sf-card p-4">
              <div className="flex items-center">
                <div className="grid h-10 w-10 flex-shrink-0 place-items-center rounded-xl bg-neon-gradient text-ink">
                  <AlertTriangle className="h-5 w-5" />
                </div>
                <div className="ml-3">
                  <p className="sf-label-mono">Totaal</p>
                  <p className="text-lg font-bold text-fg-bright">{stats.totalReports}</p>
                </div>
              </div>
            </div>
            <div className="sf-card p-4">
              <div className="flex items-center">
                <div className="grid h-10 w-10 flex-shrink-0 place-items-center rounded-xl bg-neon-cyan/[.12]">
                  <Clock className="h-5 w-5 text-neon-cyan" />
                </div>
                <div className="ml-3">
                  <p className="sf-label-mono">In behandeling</p>
                  <p className="text-lg font-bold text-neon-cyan">{stats.pendingReports}</p>
                </div>
              </div>
            </div>
            <div className="sf-card p-4">
              <div className="flex items-center">
                <div className="grid h-10 w-10 flex-shrink-0 place-items-center rounded-xl bg-neon-lime/[.12]">
                  <Check className="h-5 w-5 text-neon-lime" />
                </div>
                <div className="ml-3">
                  <p className="sf-label-mono">Opgelost</p>
                  <p className="text-lg font-bold text-neon-lime">{stats.reviewedReports}</p>
                </div>
              </div>
            </div>
            <div className="sf-card p-4">
              <div className="flex items-center">
                <div className="grid h-10 w-10 flex-shrink-0 place-items-center rounded-xl bg-neon-violet/[.12]">
                  <Shield className="h-5 w-5 text-neon-violet" />
                </div>
                <div className="ml-3">
                  <p className="sf-label-mono">Recent</p>
                  <p className="text-lg font-bold text-fg-bright">{stats.reportsToday}</p>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Simple info */}
        <div className="sf-notice-info">
          Hier vind je alle ingediende gebruiker rapporten. Klik op "Bekijken" om details te zien en acties uit te voeren.
        </div>

        {/* Report Tabs */}
        <div className="sf-card">
          <div className="border-b border-white/[.08]">
            <nav className="-mb-px flex">
              <button
                onClick={() => setActiveTab('pending')}
                className={`py-4 px-6 border-b-2 font-medium text-sm transition-colors ${
                  activeTab === 'pending'
                    ? 'border-neon-cyan text-neon-cyan'
                    : 'border-transparent text-fg-muted hover:text-fg-soft'
                }`}
              >
                In behandeling
                {stats && (
                  <span className="sf-badge-cyan ml-2">
                    {stats.pendingReports}
                  </span>
                )}
              </button>
              <button
                onClick={() => setActiveTab('resolved')}
                className={`py-4 px-6 border-b-2 font-medium text-sm transition-colors ${
                  activeTab === 'resolved'
                    ? 'border-neon-cyan text-neon-cyan'
                    : 'border-transparent text-fg-muted hover:text-fg-soft'
                }`}
              >
                Opgelost
                {stats && (
                  <span className="sf-badge-lime ml-2">
                    {stats.reviewedReports}
                  </span>
                )}
              </button>
            </nav>
          </div>
        </div>

        {/* Reports List */}
        <div className="sf-card overflow-hidden">
          {isLoading ? (
            <div className="flex items-center justify-center py-12">
              <LoadingSpinner />
            </div>
          ) : reports.length === 0 ? (
            <div className="text-center py-12">
              <AlertTriangle className="mx-auto h-12 w-12 text-fg-faint" />
              <h3 className="mt-2 text-sm font-semibold text-fg-bright">
                {activeTab === 'pending' ? 'Geen rapporten in behandeling' : 'Geen opgeloste rapporten'}
              </h3>
              <p className="mt-1 text-sm text-fg-muted">
                {activeTab === 'pending'
                  ? 'Er zijn momenteel geen rapporten die behandeling vereisen.'
                  : 'Er zijn nog geen rapporten opgelost.'
                }
              </p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full">
                <thead className="border-b border-white/[.08] bg-white/[.02]">
                  <tr>
                    <th className="px-6 py-3 text-left font-mono text-[11px] font-medium uppercase tracking-[.16em] text-fg-meta">
                      Gerapporteerde gebruiker
                    </th>
                    <th className="px-6 py-3 text-left font-mono text-[11px] font-medium uppercase tracking-[.16em] text-fg-meta">
                      Categorie
                    </th>
                    <th className="px-6 py-3 text-left font-mono text-[11px] font-medium uppercase tracking-[.16em] text-fg-meta">
                      Status
                    </th>
                    <th className="px-6 py-3 text-left font-mono text-[11px] font-medium uppercase tracking-[.16em] text-fg-meta">
                      Datum
                    </th>
                    <th className="px-6 py-3 text-left font-mono text-[11px] font-medium uppercase tracking-[.16em] text-fg-meta">
                      Acties
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {reports.map((report) => (
                    <tr key={report.id} className="border-b border-white/[.06] last:border-b-0 transition-colors hover:bg-white/[.03]">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <div className="flex-shrink-0 h-10 w-10">
                            <div className="h-10 w-10 rounded-full bg-avatar-cyan grid place-items-center">
                              <span className="text-sm font-bold text-ink">
                                {report.reported.username.charAt(0).toUpperCase()}
                              </span>
                            </div>
                          </div>
                          <div className="ml-4">
                            <div className="text-sm font-medium text-fg-bright">
                              {report.reported.username}
                            </div>
                            <div className="font-mono text-[11px] text-fg-meta">
                              ID: {report.reported.id}
                            </div>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className="inline-flex items-center rounded-lg bg-white/[.06] px-2.5 py-1 font-mono text-[11px] font-semibold uppercase text-fg-body">
                          Rapport
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={report.reviewedAt ? 'sf-badge-lime' : 'sf-badge-cyan'}>
                          {report.reviewedAt ? 'Opgelost' : 'In behandeling'}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-fg-body">
                        {new Date(report.createdAt).toLocaleDateString('nl-NL')}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                        <button
                          onClick={() => setMessageModal({
                            userId: report.reported.id.toString(),
                            username: report.reported.username
                          })}
                          className="text-neon-cyan hover:bg-white/[.06] mr-3 p-1 rounded-md transition-colors"
                          title="Bekijk berichten"
                        >
                          <Eye className="h-4 w-4" />
                        </button>
                        <button
                          onClick={() => setSelectedReport(report)}
                          className="sf-btn-secondary px-3 py-1.5 text-sm"
                        >
                          Bekijken
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Report Detail Modal */}
        {selectedReport && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-canvas/70 backdrop-blur-sm">
            <div className="bg-surface border border-white/[.08] rounded-card shadow-panel w-full max-w-2xl max-h-[90vh] overflow-y-auto">
              <div className="px-6 py-4 border-b border-white/[.08]">
                <h3 className="text-lg font-bold text-fg-bright">
                  Rapport Details
                </h3>
              </div>

              <div className="px-6 py-4 space-y-4">
                {/* Basic Info */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="sf-label-mono block mb-1">Gerapporteerde gebruiker</label>
                    <p className="text-sm text-fg">{selectedReport.reported.username}</p>
                  </div>
                  <div>
                    <label className="sf-label-mono block mb-1">Rapporteur</label>
                    <p className="text-sm text-fg">{selectedReport.reporter.username}</p>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="sf-label-mono block mb-1">Type</label>
                    <span className="inline-flex items-center rounded-lg bg-white/[.06] px-2.5 py-1 font-mono text-[11px] font-semibold uppercase text-fg-body">
                      Gebruiker Rapport
                    </span>
                  </div>
                  <div>
                    <label className="sf-label-mono block mb-1">Status</label>
                    <span className={selectedReport.reviewedAt ? 'sf-badge-lime' : 'sf-badge-cyan'}>
                      {selectedReport.reviewedAt ? 'Opgelost' : 'In behandeling'}
                    </span>
                  </div>
                </div>

                {/* Description */}
                <div>
                  <label className="sf-label-mono block mb-1">Reden</label>
                  <p className="mt-1 text-sm text-fg-body whitespace-pre-wrap">{selectedReport.notes}</p>
                </div>

                {/* Admin Notes */}
                {selectedReport.adminNotes && (
                  <div>
                    <label className="sf-label-mono block mb-1">Admin notities</label>
                    <p className="mt-1 text-sm text-fg-body whitespace-pre-wrap">{selectedReport.adminNotes}</p>
                  </div>
                )}

                <div className="font-mono text-[11px] text-fg-meta">
                  Ingediend op: {new Date(selectedReport.createdAt).toLocaleString('nl-NL')}
                  {selectedReport.reviewedAt && (
                    <span className="ml-4">
                      Gereviewed op: {new Date(selectedReport.reviewedAt).toLocaleString('nl-NL')}
                    </span>
                  )}
                </div>
              </div>

              <div className="px-6 py-4 border-t border-white/[.08] flex justify-between">
                <div className="flex space-x-2">
                  {!selectedReport.reviewedAt ? (
                    <>
                      <button
                        onClick={() => updateReportStatus(selectedReport.id, 'reviewed')}
                        className="sf-btn-accept px-3 py-1.5 text-xs"
                      >
                        Markeer als opgelost
                      </button>
                      <button
                        onClick={() => suspendUser(selectedReport.reported.id, 'Reported by user')}
                        className="sf-btn-destructive px-3 py-1.5 text-xs"
                      >
                        Gebruiker opschorten
                      </button>
                    </>
                  ) : (
                    <div className="font-mono text-[11px] text-fg-meta">
                      <p>Rapport opgelost op: {new Date(selectedReport.reviewedAt).toLocaleString('nl-NL')}</p>
                      {selectedReport.reviewer && (
                        <p className="mt-1">Door: {selectedReport.reviewer.username}</p>
                      )}
                    </div>
                  )}
                  {selectedReport.reported.suspendedAt && (
                    <button
                      onClick={() => unsuspendUser(selectedReport.reported.id)}
                      className="sf-btn-secondary px-3 py-1.5 text-xs text-neon-cyan"
                    >
                      Gebruiker herstellen
                    </button>
                  )}
                </div>
                <button
                  onClick={() => setSelectedReport(null)}
                  className="sf-btn-secondary px-4 py-2 text-sm"
                >
                  Sluiten
                </button>
              </div>
            </div>
          </div>
        )}

        {/* User Message Modal */}
        {messageModal && (
          <UserMessageModal
            userId={messageModal.userId}
            username={messageModal.username}
            isOpen={!!messageModal}
            onClose={() => setMessageModal(null)}
          />
        )}
      </div>
    </AdminLayout>
  );
};

export default AdminReportsPage;
